/* eslint-disable react/jsx-no-bind */
import { useState, useCallback } from 'react'
import {
	Col,
	Row,
	Typography,
	Input,
	Drawer,
	Badge,
	Button,
	Space,
	Radio,
} from 'antd'
import AddClientForm from 'container/AddClientForm'
import ClientsTable from 'container/ClientsTable'
import { RiFilterLine } from 'react-icons/ri'
import downloadUsingAPI from 'utils/downloadUsingAPI'
import exportClient from 'api/clients/exportClient'
import moment from 'moment'
import ClientsFilterOverlay from 'component/Clients/ClientsFilterOverlay'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setClientArchivedView, setClientQuery } from 'slices/client'

const { Title } = Typography
const { Search } = Input

const ClientsPageLayout = () => {
	/**
	 * Local state for search query
	 */
	// const [query, setQuery] = useState('')
	// const [active, setActive] = useState<boolean | undefined>(undefined)
	// const [suspended, setSuspended] = useState<boolean | undefined>(undefined)
	// const [inOnboarding, setInOnboarding] = useState<boolean | undefined>(
	// 	undefined
	// )
	// const [onBoardingStep, setOnBoardingStep] = useState<number | undefined>(
	// 	undefined
	// )

	const query = useAppSelector(state => state.client.query)
	const active = useAppSelector(state => state.client.active)
	const suspended = useAppSelector(state => state.client.suspended)
	const onBoarding = useAppSelector(state => state.client.onBoarding)
	const onBoardingStep = useAppSelector(state => state.client.onBoardingStep)
	const plan = useAppSelector(state => state.client.plan)
	const platformMode = useAppSelector(state => state.client.platformMode)
	const archivedView = useAppSelector(state => state.client.archivedView)
	const fieldFilters = useAppSelector(state => state.client.fieldFilters)
	const fieldFiltersParam = fieldFilters.length
		? JSON.stringify(fieldFilters)
		: undefined
	const activeFilterCount =
		[active, suspended, onBoarding, onBoardingStep, plan, platformMode].filter(
			item => item !== undefined
		).length + fieldFilters.length

	const dispatch = useAppDispatch()

	// const totalCount = useAppSelector(state => state.client.totalCount)

	const [filtersOpen, setFiltersOpen] = useState(false)
	const [drawer, setDrawer] = useState(false)
	function openDrawer() {
		setDrawer(true)
	}
	function closeDrawer() {
		setDrawer(false)
	}

	const setQuery = useCallback(
		(value: string) => {
			dispatch(setClientQuery(value))
		},
		[dispatch]
	)

	/**
	 * Exporting Related State & Methods
	 */

	const [exporting, setExporting] = useState(false)

	const handleExport = useCallback(async () => {
		try {
			setExporting(true)
			await downloadUsingAPI(
				await exportClient({
					query,
					...(active !== null ? { active } : {}),
					...(suspended !== null ? { suspended } : {}),
					...(onBoarding !== null ? { onBoarding } : {}),
					...(onBoardingStep !== null ? { onBoardingStep } : {}),
					...(plan !== null ? { plan } : {}),
					...(platformMode !== null ? { platformMode } : {}),
					archived: archivedView,
					fieldFilters: fieldFiltersParam,
				}),
				`WHATCX-CLIENT-${moment().format('YYYY-MM-DD hh-mm-ss A')}.csv`
			)
		} finally {
			setExporting(false)
		}
	}, [
		query,
		active,
		suspended,
		onBoarding,
		onBoardingStep,
		plan,
		platformMode,
		archivedView,
		fieldFiltersParam,
	])

	return (
		<>
			<Row justify='space-between' align='middle' gutter={[16, 16]}>
				<Col>
					<Space align='center' size='middle'>
						<Title level={2} style={{ marginBottom: 0 }}>
							Clients
						</Title>
						<Radio.Group
							value={archivedView ? 'archived' : 'current'}
							onChange={event =>
								dispatch(setClientArchivedView(event.target.value === 'archived'))
							}
						>
							<Radio.Button value='current'>Current</Radio.Button>
							<Radio.Button value='archived'>Archived</Radio.Button>
						</Radio.Group>
					</Space>
				</Col>

				<Col>
					<Space>
						<Badge count={activeFilterCount} size='small'>
							<Button type='default' onClick={() => setFiltersOpen(true)}>
								<RiFilterLine />
							</Button>
						</Badge>
						<Search
							placeholder='Search name, email, mobile, or client ID'
							onSearch={setQuery}
							style={{ width: 340 }}
							allowClear
							enterButton
						/>
						<Button type='default' onClick={handleExport} loading={exporting}>
							Export
						</Button>
						<Button type='primary' onClick={openDrawer}>
							Add Client
						</Button>
					</Space>
				</Col>

				<Col span={24}>
					<ClientsTable
						active={active}
						suspended={suspended}
						query={query}
						onBoarding={onBoarding}
						onBoardingStep={onBoardingStep}
						plan={plan}
						platformMode={platformMode}
						archived={archivedView}
						fieldFilters={fieldFiltersParam}
					/>
				</Col>
			</Row>
			<Drawer
				title='Filter clients'
				placement='right'
				width={440}
				closable
				visible={filtersOpen}
				onClose={() => setFiltersOpen(false)}
			>
				<ClientsFilterOverlay />
			</Drawer>
			<Drawer
				title='Add New Client'
				placement='right'
				width='70rem'
				closable
				visible={drawer}
				onClose={closeDrawer}
				destroyOnClose
			>
				<AddClientForm closeDrawer={closeDrawer} />
			</Drawer>
		</>
	)
}

export default ClientsPageLayout
